import React, { useEffect, useState } from "react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import { Button, Dropdown, Form, Grid, Modal, Icon } from "semantic-ui-react";
import { ApproverAssetSelectionModal } from "../ApproverAssetSelectionModal/ApproverAssetSelectionModal";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentLanguage, getDateFormat } from "services/common";
import { toast } from "components/Toast/Toast";

const ApproverAssetSelector = ({
  campaign,
  assets,
  approvers,
  isApproversLoading,
  t,
  naked = false,
}) => {
  const dispatch = useDispatch();
  const approvals = useSelector((state) => state.approvals);
  const currentTime = new Date();
  const twoDaysFromCurrentTime = new Date(currentTime.getTime() + 2 * 24 * 60 * 60 * 1000);
  const [selectedDate, setSelectedDate] = useState(twoDaysFromCurrentTime);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [approverAssetMap, setApproverAssetMap] = useState({});
  const [canSave, setCanSave] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  useEffect(() => {
    let someoneIsMissingAssets = false;
    const sameSize = selectedApprovers.length === Object.keys(approverAssetMap).length;
    const atLeastOneApprover = selectedApprovers.length > 0;
    const atLeastOneKeyInApproverAssets = Object.keys(approverAssetMap).length > 0;
    for (const [key, value] of Object.entries(approverAssetMap)) {
      if (selectedApprovers.includes(key)) {
        const hasAtLeastOneAsset = value.selectedItems.length > 0;
        if (!hasAtLeastOneAsset) {
          someoneIsMissingAssets = true;
        }
      }
    }
    setCanSave(
      atLeastOneApprover && !someoneIsMissingAssets && atLeastOneKeyInApproverAssets && sameSize
    );
  }, [approverAssetMap, selectedApprovers]);

  const convertApproversToDropdownOptions = (approvers) => {
    return approvers.map((approver) => {
      return {
        key: approver.id,
        value: approver.id,
        text: `${approver.name} | ${approver.email}`,
      };
    });
  };

  const handleSave = async () => {
    try {
      const campaignViewerIds = approvers
        .filter((approver) => selectedApprovers.includes(approver.id))
        .map((approver) => {
          return approver.userID;
        });
      const response = await dispatch.approvals.create({
        campaign: campaign,
        approverAssetMap: approverAssetMap,
        deadline: selectedDate,
        campaignViewerIds: campaignViewerIds,
      });
      if (response && response.length > 0) {
        toast(undefined, t("approvals_toast_approvals_create_success"));
      }
      handleClose();
      setSelectedApprovers([]);
      setApproverAssetMap({});
    } catch (error) {
      toast("error", `${t("approvals_toast_approvals_create_error")}: ${error}`);
    }
  };

  const handleApproverChange = (e, data) => {
    setSelectedApprovers(data.value);
  };

  const handleAssetChange = (approver, selectedItems) => {
    setApproverAssetMap((prevMap) => ({
      ...prevMap,
      [approver.id]: { approver: approver, selectedItems: selectedItems },
    }));

    if (selectedItems.length === 0) {
      let state = { ...approverAssetMap };
      delete state[approver.id];
      setApproverAssetMap(state);
    }
  };

  return (
    <Modal
      trigger={
        naked ? (
          <button
            style={{ fontSize: "1rem", color: "#4700a3" }}
            type="button"
            className="naked-button"
            onClick={handleOpen}
          >
            {" "}
            <Icon name="user outline"></Icon>
            {t("campaigns_approver_asset_selector_select_approvers")}
          </button>
        ) : (
          <Button basic className="bmo-purple" onClick={handleOpen} disabled={isApproversLoading}>
            <Icon name="user"></Icon>
            {t("campaigns_approver_asset_selector_select_approvers")}
          </Button>
        )
      }
      open={isOpen}
      onClose={handleClose}
    >
      <Modal.Header>{t("approvals_select_approvers_modal_select_header")}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <h4>{t("approvals_select_approvers_modal_select_deadline")}</h4>
            <SemanticDatepicker
              onChange={(e, data) => {
                if (data.value) {
                  setSelectedDate(data.value);
                }
              }}
              firstDayOfWeek={1}
              format={getDateFormat()}
              autoComplete="off"
              minDate={currentTime}
              locale={getCurrentLanguage()}
              value={selectedDate}
            />
          </Form.Field>
          <Form.Field>
            <h4>{t("approvals_select_approvers_modal_select_select_approvers")}</h4>
            <Dropdown
              fluid
              multiple
              search
              selection
              options={convertApproversToDropdownOptions(approvers)}
              value={selectedApprovers}
              onChange={handleApproverChange}
            />
          </Form.Field>
          <Form.Field>
            <Grid style={{ marginTop: "2rem", marginBottom: "2rem" }}>
              {selectedApprovers.map((approver) => (
                <ApproverAssetSelectionModal
                  key={approver}
                  approver={approvers.find((appr) => appr.id === approver)}
                  assets={assets}
                  t={t}
                  handleAssetChange={handleAssetChange}
                  approverAssetMap={approverAssetMap}
                />
              ))}
            </Grid>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          style={{
            backgroundColor: "#4700a3",
            color: "white",
          }}
          onClick={handleClose}
        >
          {t("approvals_select_approvers_modal_select_cancel")}
        </Button>
        <Button
          style={{
            backgroundColor: !approvals.isLoading && canSave ? "#4700a3" : "#767676",
            color: "white",
          }}
          onClick={handleSave}
          disabled={approvals.isLoading || !canSave}
        >
          {t("approvals_select_approvers_modal_select_inform_approvers")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
export default ApproverAssetSelector;
